import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Snackbar, Button, Box, CircularProgress, Typography, Portal } from '@mui/material';
import { Close as CloseIcon, Update as UpdateIcon } from '@mui/icons-material';

const POST_UPDATE_COOLDOWN = 120_000; // 2min grace period after an update reload

const PWAUpdatePrompt = () => {
  const [updating, setUpdating] = useState(false);

  // After an update reload, suppress the prompt so the browser can
  // settle with the latest SW without showing a chain of prompts.
  const [coolingDown, setCoolingDown] = useState(() => {
    const lastUpdate = sessionStorage.getItem('pwa-update-ts');
    if (lastUpdate && Date.now() - Number(lastUpdate) < POST_UPDATE_COOLDOWN) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (!coolingDown) return;
    const remaining = (() => {
      const lastUpdate = sessionStorage.getItem('pwa-update-ts');
      if (!lastUpdate) return POST_UPDATE_COOLDOWN;
      return Math.max(0, POST_UPDATE_COOLDOWN - (Date.now() - Number(lastUpdate)));
    })();
    const timer = setTimeout(() => setCoolingDown(false), remaining);
    return () => clearTimeout(timer);
  }, [coolingDown]);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW() {
      // Update checks happen on visibility change (app foregrounded) — no polling needed
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  // Also check for updates when the app returns to foreground (tab switch, phone unlock)
  useEffect(() => {
    const checkOnFocus = () => {
      if (document.visibilityState === 'visible') {
        navigator.serviceWorker?.getRegistration().then((reg) => reg?.update());
      }
    };
    document.addEventListener('visibilitychange', checkOnFocus);
    return () => document.removeEventListener('visibilitychange', checkOnFocus);
  }, []);

  // Suppress duplicate prompts: once the user acted, don't show again
  // for the rest of this cooldown window (survives reload)
  const [dismissed, setDismissed] = useState(() => {
    const ts = sessionStorage.getItem('pwa-dismiss-ts');
    return !!ts && Date.now() - Number(ts) < POST_UPDATE_COOLDOWN;
  });

  const handleUpdate = async () => {
    setNeedRefresh(false);
    setDismissed(true);
    setUpdating(true);

    const now = String(Date.now());
    sessionStorage.setItem('pwa-update-ts', now);
    sessionStorage.setItem('pwa-dismiss-ts', now);

    let reloading = false;
    const reload = () => {
      if (!reloading) {
        reloading = true;
        window.location.reload();
      }
    };

    // Signal 1: native controllerchange
    navigator.serviceWorker?.addEventListener('controllerchange', reload);

    const registration = await navigator.serviceWorker?.getRegistration();
    const waiting = registration?.waiting;

    if (waiting) {
      // Signal 2: watch the waiting worker's state directly — more reliable
      // than controllerchange in Chrome standalone PWA mode
      waiting.addEventListener('statechange', () => {
        if (waiting.state === 'activated') reload();
      });
      waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      updateServiceWorker(true);
    }

    // Signal 3: force reload after 2s if neither event fired
    setTimeout(reload, 2000);
  };

  const handleClose = () => {
    setNeedRefresh(false);
    setDismissed(true);
    sessionStorage.setItem('pwa-dismiss-ts', String(Date.now()));
  };

  return (
    <>
      <Snackbar
        open={needRefresh && !coolingDown && !dismissed}
        message="A new version is available"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <>
            <Button color="inherit" size="small" onClick={handleClose} startIcon={<CloseIcon />}>
              Later
            </Button>
            <Button color="primary" size="small" onClick={handleUpdate} startIcon={<UpdateIcon />}>
              Update Now
            </Button>
          </>
        }
      />

      {updating && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              Updating...
            </Typography>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default PWAUpdatePrompt;
